import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { ButtonPrimitive, CHECKERBOARD_BACKGROUND, IconButton, Input, Stack, Text } from '@figma/fpl-components';
import { Icon24Close, Icon24Plus } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { useTextIterator } from '@/hooks/useTextIterator';
import { useMediaIterator } from '@/components/Recipe/DesignApp/useMediaIterator';
import { AssetThumbnail } from '@/components/Common/AssetThumbnail';
import { FileUploader } from '@/components/Common/FileUploader/ToolsView/FileUploader';
import { useResolvedAssets } from '@/state/workflow.state';
import { thumbnailSource } from '@/utils/assetUrls';
import { I18N_KEYS } from '@/language/keys';
import { LIST_MAX_CHARS } from '@/components/Nodes/consts/validations.consts';
import { styles } from './ToolIterators.styles';
import { ToolInputRow } from './ToolInputControls';
import { ToolInputList, ToolInputListRow } from './ToolInputList';
import type { ControlProps } from './ToolInputControls';
import type { ComponentProps } from 'react';
import type { FileKind } from 'web';
import type { FileType } from '@/hooks/useUploadMediaAsset';
import type { MediaIteratorInput } from '@/types/design-app.types';

function IteratorCount({ count, maxCount, disabled }: { count: number; maxCount: number; disabled: boolean }) {
  return (
    <Text.span variant="body-medium" color={disabled ? 'disabled' : 'secondary'}>
      {`${count}/${maxCount}`}
    </Text.span>
  );
}

type IteratorProps = ControlProps &
  Pick<ComponentProps<typeof ToolInputRow>, 'description' | 'required'> & {
    value: { options: string[] };
    onChange: (value: { options: string[] }) => void;
  };

export function ToolTextIterator({
  id,
  label,
  required,
  value,
  onChange,
  disabled,
  error,
  description,
}: IteratorProps) {
  const { t } = useTranslation();
  const {
    options,
    maxOptions,
    inputRefs,
    handleValueChange,
    handleAddItem,
    handleEnter,
    handleTab,
    handleDelete,
    handleDeleteAndFocusPrev,
  } = useTextIterator({ value, onChange });
  const errorId = `${id}-error`;
  const handleKeyDown = (evt, index, option) => {
    if (disabled) return;

    if (evt.key === 'Escape') {
      evt.preventDefault();
      evt.stopPropagation();
      evt.currentTarget.blur();
      return;
    }

    if (evt.key === 'Tab') {
      evt.preventDefault();
      evt.stopPropagation();
      return handleTab(index, evt.shiftKey ? 'prev' : 'next');
    }

    if (evt.key === 'Enter') {
      evt.preventDefault();
      evt.stopPropagation();
      if (option.length === 0) return;
      return handleEnter(index);
    }

    if (evt.key === 'Backspace' && option.length === 0 && options.length > 1) {
      evt.preventDefault();
      evt.stopPropagation();
      handleDeleteAndFocusPrev(index);
    }
  };

  return (
    <ToolInputList
      label={label}
      required={required}
      disabled={disabled}
      description={description}
      count={<IteratorCount count={options.length} maxCount={maxOptions} disabled={disabled} />}
      add={{
        label: t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ADD_ITEM),
        onClick: handleAddItem,
        disabled: options.length >= maxOptions,
      }}
      scrollable
      footer={
        error && (
          <Text.div id={errorId} variant="body-medium" color="danger" role="alert">
            {error}
          </Text.div>
        )
      }
    >
      {options.map((option, index) => {
        const placeholder = t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.PLACEHOLDER, {
          index: index + 1,
        });
        return (
          <ToolInputListRow
            key={index}
            remove={{
              label: t(I18N_KEYS.GENERAL.ARIA.DELETE_NAMED_ITEM, { name: placeholder }),
              disabled: disabled || options.length <= 1,
              onClick: () => handleDelete(index),
            }}
          >
            <Input
              id={`${id}-${index}`}
              aria-invalid={index === 0 && !!error}
              aria-describedby={index === 0 && error ? errorId : undefined}
              ref={(input) => {
                inputRefs.current[index] = input;
              }}
              maxLength={LIST_MAX_CHARS}
              size="lg"
              value={option}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(next) => handleValueChange(next, index)}
              onKeyDown={(evt) => handleKeyDown(evt, index, option)}
              onFocus={(event) => event.currentTarget.select()}
            />
          </ToolInputListRow>
        );
      })}
    </ToolInputList>
  );
}

interface MediaIteratorProps extends Omit<IteratorProps, 'value' | 'onChange'> {
  value: MediaIteratorInput['value'];
  onChange: (value: MediaIteratorInput['value']) => void;
  acceptedFileType: FileType;
  setIsUploading: (isUploading: boolean) => void;
}

export function ToolMediaIterator({
  id,
  label,
  required,
  value,
  onChange,
  disabled,
  error,
  description,
  acceptedFileType,
  setIsUploading,
}: MediaIteratorProps) {
  const { t } = useTranslation();
  const emit = useCallback((_: string, next: MediaIteratorInput['value']) => onChange(next), [onChange]);
  const { files, maxFiles, canAdd, handleRemove, open, uploadState } = useMediaIterator({
    id,
    value,
    onChange: emit,
    acceptedFileType,
    disabled,
    isLoading: false,
    setIsUploading,
  });

  return (
    <Stack.Col gap={cssVars.spacer.x2}>
      <ToolInputRow
        label={label}
        required={required}
        disabled={disabled}
        description={description}
        headerActions={<IteratorCount count={files.length} maxCount={maxFiles} disabled={disabled} />}
      />
      <FileUploader id={id} uploadState={uploadState} disabled={disabled} error={!!error} helperText={error}>
        {files.length > 0 ? (
          <>
            {canAdd && (
              <ButtonPrimitive
                aria-label={t(I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.ADD_FILE)}
                onClick={open}
                {...stylex.props(styles.mediaTile, styles.mediaAdd)}
              >
                <Icon24Plus />
              </ButtonPrimitive>
            )}
            {files.map((file, index) => (
              <MediaIteratorThumbnail
                key={`${file.url}-${index}`}
                file={file}
                index={index}
                onRemove={handleRemove}
                disabled={disabled}
              />
            ))}
          </>
        ) : undefined}
      </FileUploader>
    </Stack.Col>
  );
}

function MediaIteratorThumbnail({
  file,
  index,
  onRemove,
  disabled,
}: {
  file: FileKind;
  index: number;
  onRemove: (index: number) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const resolvedAssets = useResolvedAssets();

  return (
    <div {...stylex.props(styles.mediaTile, styles.mediaThumbnail)} style={{ background: CHECKERBOARD_BACKGROUND }}>
      <AssetThumbnail
        source={thumbnailSource(file, resolvedAssets)}
        alt={`file-${index}`}
        {...stylex.props(styles.mediaImage)}
      />
      {!disabled && (
        <div {...stylex.props(styles.mediaRemove)}>
          <IconButton
            aria-label={t(I18N_KEYS.GENERAL.ARIA.DELETE_NAMED_ITEM, { name: `file-${index}` })}
            onClick={() => onRemove(index)}
          >
            <Icon24Close />
          </IconButton>
        </div>
      )}
    </div>
  );
}
